
import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Grid,
  Divider,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Person as PersonIcon,
  AccountCircle as AccountIcon,
  SwapHoriz as SwapIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

const ChangeSponsor = () => {
  const [form, setForm] = useState({
    loginId: "",
    name: "",
    sponsorLoginId: "",
    sponsorName: "",
    newSponsorLoginId: "",
    newSponsorName: "",
  });

  const [searchPerformed, setSearchPerformed] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    // Simulate search - in real app, this would be an API call
    if (form.loginId) {
      setForm({
        ...form,
        name: "John Doe",
        sponsorLoginId: "SP001",
        sponsorName: "Jane Smith",
      });
      setSearchPerformed(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.newSponsorLoginId || !form.newSponsorName) {
      alert("Please fill in new sponsor details");
      return;
    }
    alert("Sponsor changed successfully!");
    // Reset form
    setForm({
      loginId: "",
      name: "",
      sponsorLoginId: "",
      sponsorName: "",
      newSponsorLoginId: "",
      newSponsorName: "",
    });
    setSearchPerformed(false);
  };

  return (
  <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Change Sponsor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update associate sponsor information
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Search Associate Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#ffffff', height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SearchIcon sx={{ color: '#536DFE' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                  Search Associate
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Login ID / Associate ID"
                    name="loginId"
                    value={form.loginId}
                    onChange={handleChange}
                    fullWidth
                    required
                    placeholder="Enter associate login ID"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSearch}
                    disabled={!form.loginId}
                    startIcon={<SearchIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '15px',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    }}
                  >
                    Search Associate
                  </Button>
                </Grid>
              </Grid>

              {searchPerformed && (
                <Box mt={3}>
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    Associate found! Details loaded below.
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Current Details Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#ffffff', height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <PersonIcon sx={{ color: '#536DFE' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                  Current Associate Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Associate Name"
                    name="name"
                    value={form.name}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5'
                      } 
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Current Sponsor
                    </Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Current Sponsor Login ID"
                    name="sponsorLoginId"
                    value={form.sponsorLoginId}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5'
                      } 
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Current Sponsor Name"
                    name="sponsorName"
                    value={form.sponsorName}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5'
                      } 
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Sponsor Form */}
        {searchPerformed && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#ffffff' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <SwapIcon sx={{ color: '#536DFE' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                    New Sponsor Information
                  </Typography>
                </Box>

                <Alert  sx={{ mb: 3, borderRadius: 2 }}>
                  Please enter the new sponsor details carefully. This action cannot be undone easily.
                </Alert>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="New Sponsor Login ID"
                        name="newSponsorLoginId"
                        value={form.newSponsorLoginId}
                        onChange={handleChange}
                        fullWidth
                        required
                        placeholder="Enter new sponsor login ID"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="New Sponsor Name"
                        name="newSponsorName"
                        value={form.newSponsorName}
                        onChange={handleChange}
                        fullWidth
                        required
                        placeholder="Enter new sponsor name"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setForm({
                              loginId: "",
                              name: "",
                              sponsorLoginId: "",
                              sponsorName: "",
                              newSponsorLoginId: "",
                              newSponsorName: "",
                            });
                            setSearchPerformed(false);
                          }}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600,
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SwapIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 4,
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                          }}
                        >
                          Change Sponsor
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ChangeSponsor;